/**
 * Utility functions for country name normalization and handling
 */

/**
 * Map of country name variations to standardized country names
 * This handles common variations, abbreviations, and misspellings
 */
const COUNTRY_NAME_MAP = {
  // United States variations
  'usa': 'USA',
  'us': 'USA',
  'u.s.': 'USA',
  'u.s.a.': 'USA',
  'united states': 'USA',
  'united states of america': 'USA',
  'america': 'USA',
  'united state': 'USA',
  
  // India variations
  'ind': 'India',
  'republic of india': 'India',
  
  // United Kingdom variations
  'uk': 'United Kingdom',
  'u.k.': 'United Kingdom',
  'great britain': 'United Kingdom',
  'britain': 'United Kingdom',
  'england': 'United Kingdom',
  'scotland': 'United Kingdom',
  'wales': 'United Kingdom',
  'northern ireland': 'United Kingdom',
  
  // Canada variations
  'can': 'Canada',
  'ca': 'Canada',
  
  // Australia variations
  'aus': 'Australia',
  'au': 'Australia',
  
  // Germany variations
  'deu': 'Germany',
  'de': 'Germany',
  'deutschland': 'Germany',
  
  // France variations
  'fra': 'France',
  'fr': 'France',
  
  // China variations
  'cn': 'China',
  'chn': 'China',
  'prc': 'China',
  'people\'s republic of china': 'China',
  
  // Japan variations
  'jp': 'Japan',
  'jpn': 'Japan',
  
  // South Korea variations
  'kr': 'South Korea',
  'kor': 'South Korea',
  'korea': 'South Korea',
  'republic of korea': 'South Korea',
  
  // Brazil variations
  'br': 'Brazil',
  'bra': 'Brazil',
  
  // Mexico variations
  'mx': 'Mexico',
  'mex': 'Mexico',
  
  // Spain variations
  'es': 'Spain',
  'esp': 'Spain',
  'españa': 'Spain',
  
  // Italy variations
  'it': 'Italy',
  'ita': 'Italy',
  'italia': 'Italy',
  
  // Russia variations
  'ru': 'Russia',
  'rus': 'Russia',
  'russian federation': 'Russia',
  
  // Netherlands variations
  'nl': 'Netherlands',
  'nld': 'Netherlands',
  'holland': 'Netherlands',
  'the netherlands': 'Netherlands',
  
  // Sweden variations
  'se': 'Sweden',
  'swe': 'Sweden',
  
  // Switzerland variations
  'ch': 'Switzerland',
  'che': 'Switzerland',
  'swiss': 'Switzerland',
  
  // Singapore variations
  'sg': 'Singapore',
  'sgp': 'Singapore',
  
  // United Arab Emirates variations
  'uae': 'UAE',
  'u.a.e.': 'UAE',
  'united arab emirates': 'UAE',
  'emirates': 'UAE',
  
  // Saudi Arabia variations
  'sa': 'Saudi Arabia',
  'sau': 'Saudi Arabia',
  'ksa': 'Saudi Arabia',
  'kingdom of saudi arabia': 'Saudi Arabia',
  
  // South Africa variations
  'za': 'South Africa',
  'zaf': 'South Africa',
  'rsa': 'South Africa',
  'republic of south africa': 'South Africa',
  
  // New Zealand variations
  'nz': 'New Zealand',
  'nzl': 'New Zealand',
  'new zeland': 'New Zealand', // Common misspelling
};

/**
 * Normalizes a country name to a standardized format
 * 
 * @param {string} countryName - The country name to normalize
 * @returns {string} - The normalized country name
 */
export const normalizeCountryName = (countryName) => {
  if (!countryName) return '';
  
  // Trim and convert to lowercase for matching
  const normalizedInput = countryName.trim().toLowerCase();
  
  // Return the mapped country name or the original with proper capitalization if not found
  return COUNTRY_NAME_MAP[normalizedInput] || 
    countryName.trim().replace(/\w\S*/g, (txt) => {
      return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
    });
};

/**
 * Extracts unique normalized country names from alumni data
 * 
 * @param {Array} alumni - Array of alumni objects
 * @returns {Array} - Array of unique normalized country names
 */
export const extractUniqueCountries = (alumni) => {
  if (!alumni || !Array.isArray(alumni)) return [];
  
  const countries = alumni
    .map(alumnus => normalizeCountryName(alumnus.emp_country))
    .filter(country => country && country.trim() !== '')
    .reduce((unique, country) => {
      if (!unique.includes(country)) {
        unique.push(country);
      }
      return unique;
    }, []);
  
  // Sort countries alphabetically
  return countries.sort();
};

/**
 * Normalizes country names in alumni data
 * 
 * @param {Array} alumni - Array of alumni objects
 * @returns {Array} - Array of alumni with normalized country names
 */
export const normalizeAlumniCountries = (alumni) => {
  if (!alumni || !Array.isArray(alumni)) return [];
  
  return alumni.map(alumnus => {
    if (!alumnus) return alumnus;
    
    return {
      ...alumnus,
      emp_country: normalizeCountryName(alumnus.emp_country)
    };
  });
};
